// components/BlogCard.tsx
import Image from "next/image";
import Link from "next/link";
import clsx from "clsx";
import { BlogCardProps } from "@/lib/Types";
import DOMPurify from "isomorphic-dompurify";
import { formatDate, stringDateFormat } from "@/lib/Utility";
// Moved outside — stable across renders
function CardWrapper({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={clsx(
        "group bg-white border border-[#ECF0F6] p-[30px] overflow-hidden h-full",
        "transition-shadow hover:shadow-[0_12px_40px_rgba(34,91,212,0.15)]",
        className,
      )}
      style={{ boxShadow: "0 0 0 1px #ECF0F6 inset" }}
    >
      {children}
    </div>
  );
}

export default function BlogCard({
  imageSrc,
  category,
  title,
  description,
  date = "08/06/2026",
  href = "#",
  className,
  slug,
}: BlogCardProps) {
  const formattedDate = formatDate(date);
  const Inner = (
    <CardWrapper className={className}>
      <div className="w-full aspect-[4.88/2.29] relative">
        {imageSrc ? (
          <Image
            src={imageSrc}
            alt={title}
            fill
            sizes="(min-width: 1024px) 553px, 100vw"
            className="object-contain object-center"
            priority
            fetchPriority="high"
          />
        ) : null}
      </div>

      <div className="flex flex-col gap-[15px]">
        <div className="inline-flex">
          <span className="px-5 py-[5px] mt-[30px] rounded-full border bg-[#F2F4F8] border-[#ECF0F6] text-[#00040b] text-[15px] leading-[140%] font-medium">
            {category}
          </span>
        </div>

        <h3
          className="text-[#00040b] clash-font font-medium leading-[160%] text-[24px]"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(title) }}
        />

        <p
          className="text-[#00040b]/70 text-[18px] leading-[160%] line-clamp-2"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
        />

        <div className="pt-[5px] text-[#00040b] text-[15px] leading-[140%] font-medium">
          {formattedDate}
        </div>
      </div>
    </CardWrapper>
  );

  if (slug) {
    return (
      <Link
        href={`/${slug}/`}
        aria-label="Read more about Slug"
        className="no-underline flex flex-col"
      >
        {Inner}
      </Link>
    );
  }

  if (href && href !== "#") {
    return (
      <Link
        href={`${href}/`}
        aria-label="Read more about Slug"
        className="no-underline flex flex-col"
      >
        {Inner}
      </Link>
    );
  }

  return Inner;
}
